import React, { createContext, useState, useEffect, ReactNode } from 'react';

export interface UserProfile {
  email: string;
  name: string;
  photo: string;
}

interface AuthContextType {
  authenticatedUser: UserProfile | null;
  setAuthenticatedUser: (user: UserProfile | null) => void;
  isAuthenticated: boolean;
  login: (user: UserProfile) => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType>({
  authenticatedUser: null,
  setAuthenticatedUser: () => {},
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authenticatedUser, setAuthenticatedUser] = useState<UserProfile | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    // Check for existing user session on component mount
    const storedUser = localStorage.getItem('authenticatedUser');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setAuthenticatedUser(user);
      setIsAuthenticated(true);
    }
  }, []);

  const login = (user: UserProfile) => {
    setAuthenticatedUser(user);
    setIsAuthenticated(true);
    localStorage.setItem('authenticatedUser', JSON.stringify(user));
  };

  const logout = () => {
    setAuthenticatedUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('authenticatedUser');
  };

  return (
    <AuthContext.Provider
      value={{
        authenticatedUser,
        setAuthenticatedUser,
        isAuthenticated,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}; 